/**
 * buildWorkloadPowerFitContent — the ONE content builder behind the
 * "Workload & Power-Source Fit" report section, shared by all four surfaces
 * (on-screen view + PDF via React, DOCX, PPTX) so they render byte-identical,
 * cited data.
 *
 * Pure function of the report's effective workload profile + the shared cited
 * catalog (`workloadTechnicalReference.ts`). No LLM, no I/O — a static /
 * data-driven section (the repo's established convention for catalog sections).
 * Explicit Unknown renders as an evidence gap with a recommendation; it never
 * borrows the `mixed` engineering table or density assumptions.
 */
import type { SiteRagReport } from '../../types/siteRag';
import { normalizeReportDashes } from '../reportRendering/normalizeReportDashes';
import { effectiveWorkloadProfile, getWorkloadProfile } from '../workloadProfile';
import {
  WORKLOAD_TECH_REFERENCE,
  BTM_SOURCE_REFERENCE,
  type SourceId,
} from '../workloadTechnicalReference';
import { projectDerivedTableReferences } from './derivedTableReferences';

export interface WorkloadPowerFitContent {
  profileLabel: string;
  intro: string;
  /** Label→value rows for the site's workload technical profile (cited inline). */
  techRows: Array<{ label: string; value: string }>;
  /** The workload's behind-the-meter power fit, cited. */
  btmFitLine: string;
  /** BTM source comparison table (headers + string rows). */
  btmTable: { headers: string[]; rows: string[][] };
}

export function buildWorkloadPowerFitContent(report: SiteRagReport): WorkloadPowerFitContent {
  const profile = effectiveWorkloadProfile(report.projectIntentAtGen);
  if (profile === 'unknown') {
    return normalizeReportDashes({
      profileLabel: 'Unknown / not yet decided',
      intro: 'The workload is explicitly recorded as unknown. Workload-specific rack density, cooling, network, and power-profile conclusions are withheld until the operator confirms the intended use.',
      techRows: [
        { label: 'Status', value: 'Unknown / not yet decided' },
        { label: 'Recommended next step', value: 'Confirm the primary workload with the operator, then regenerate the workload and power-source fit.' },
      ],
      btmFitLine: 'No workload-specific behind-the-meter fit is asserted while the workload remains unknown.',
      btmTable: {
        headers: ['BTM source', 'Capacity factor', 'LCOE', 'Lead time', 'Dispatchability', 'Best-fit workloads'],
        rows: [],
      },
    });
  }
  const referenceProjection = projectDerivedTableReferences(report, { forceWorkload: true });
  const profileLabel = getWorkloadProfile(profile).label;
  const t = WORKLOAD_TECH_REFERENCE[profile];
  const cite = (id: SourceId): string => {
    const referenceNumber = referenceProjection.workloadReferenceNumbers[id];
    if (!referenceNumber) {
      throw new Error(`Workload source "${id}" was not projected into report.references.`);
    }
    return `[Ref ${referenceNumber}]`;
  };

  // HOUSE STYLE: zero U+2014 on a rendered report surface (emDashRenderSanitizer gate).
  // `prepareReportForRender` normalizes the REPORT OBJECT, but this section's prose comes
  // from STATIC module tables (workloadAssumptions / workloadTechnicalReference), which never
  // cross that boundary — so normalize at this single choke point rather than hand-scrubbing
  // every source string. normalizeReportDashes is deep + reference-preserving: an em-dash-free
  // content object is returned by identity, so the render memo stays hot.
  return normalizeReportDashes({
    profileLabel,
    intro:
      `This site's declared workload is ${profileLabel}. The indicative rack-density envelope informs candidate cooling ` +
      'architectures (conditional on actual project/OEM thermal design), while its load factor and power-quality profile determine which behind-the-meter (BTM) ' +
      'power sources can serve it at the required firmness. Supporting sources are listed in Appendix & References.',
    techRows: [
      { label: 'Rack density (kW/rack)', value: `${t.rackDensityKw} ${cite(t.rackDensitySource)}` },
      { label: 'Cooling required', value: `${t.coolingRequired} ${cite(t.coolingSource)}` },
      { label: 'Typical PUE', value: `${t.typicalPue} ${cite(t.pueSource)}` },
      { label: 'Power profile', value: `${t.powerProfile} ${cite(t.powerProfileSource)}` },
    ],
    btmFitLine: `${t.btmFit} ${cite(t.btmFitSource)}`,
    btmTable: {
      headers: ['BTM source', 'Capacity factor', 'LCOE', 'Lead time', 'Dispatchability', 'Best-fit workloads'],
      rows: BTM_SOURCE_REFERENCE.map((b) => [
        b.label,
        `${b.capacityFactor} ${cite(b.capacityFactorSource)}`,
        b.lcoeSource ? `${b.lcoe} ${cite(b.lcoeSource)}` : b.lcoe,
        b.leadTime,
        b.dispatchable,
        `${b.bestFitWorkloads} ${cite(b.noteSource)}`,
      ]),
    },
  });
}
